import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { of } from 'rxjs/internal/observable/of';
import { takeUntil, timeout } from 'rxjs/operators';

import { ElectionService } from 'src/app/services/election.service';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { Subject } from 'rxjs';
import { IShadowTableData } from 'src/app/interfaces/IShadowTableData';
import { PollingCandidateCount } from 'src/app/models/PollingCandidateCount';
import { ITableHeader } from 'src/app/interfaces/ITableHeader';


@Component({
  selector: 'first-preference-by-place-and-vote-type',
  templateUrl: './first-preference-by-place-and-vote-type.component.html',
  styleUrls: ['./first-preference-by-place-and-vote-type.component.scss']
})
export class FirstPreferenceByPlaceAndVoteTypeComponent implements AfterViewInit, OnDestroy {

  @Input() title = 'First preferences by voting centre & vote type';


  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();


  public columns: ITableHeader[] = [];
  public shadowTable: IShadowTableData[][] = [];
  public dataSource: any = null;
  public displayedColumns: string[] = [];
  public tableDistrictKey = '';

  constructor(
    private electionService: ElectionService,
    private cdr: ChangeDetectorRef
  ) {
    this.electionService.dataLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
      if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
        this.loadChart();
      }
    });
    this.electionService.pageChanged$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
      this.loadChart();
    });
  }


  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic
      ]);
    }
    return false;
  }


  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.loadChart();
    }
  }

  trackByDistrictAndColumn(index: number, item: { column: string }): string {
    return (this.tableDistrictKey || '') + '_' + item.column;
  }

  private loadChart(): void {
    const page = this.electionService.selectedPage;
    if (page.pageType !== PageTypeEnum.CountHADistrict || !page.countDistrict) {
      this.tableDistrictKey = '';
      this.columns = [];
      this.shadowTable = [];
      this.displayedColumns = [];
      this.dataSource = new MatTableDataSource([]);
      this.cdr.markForCheck();
      return;
    }
    this.columns = [];
    this.shadowTable = [];
    this.displayedColumns = [];
    this.dataSource = new MatTableDataSource([]);
    const election = this.electionService.electionMap.get(page.electionDateString ?? '');
    const foundDistrict = election?.electionCount?.getDistrict(page.countDistrict);
    if (foundDistrict) {
      this.tableDistrictKey = page.countDistrict ?? '';
      this.columns.push({
        column: '0',
        label: 'Polling Location',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 120,
        isBold: false,
        isRightJustify: false,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });

      foundDistrict.candidates.forEach((candidate, index) => {
        this.columns.push({
          column: (index + 1).toString(),
          label: candidate.candidateName,
          labelTwo: `(${candidate.partyId})`,
          isTwoRowLabel: true,
          minWidth: 120,
          isBold: false,
          isRightJustify: true,
          isPercent: false,
          isColour: false,
          isColourBar: false,
          colourCode: candidate.colourCode
        });
      });
      this.columns.push({
        column: (foundDistrict.candidates.length + 1).toString(),
        label: 'Formal',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 80,
        isBold: false,
        isRightJustify: true,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });
      this.columns.push({
        column: (foundDistrict.candidates.length + 2).toString(),
        label: 'Informal',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 70,
        isBold: false,
        isRightJustify: true,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });
      this.columns.push({
        column: (foundDistrict.candidates.length + 3).toString(),
        label: 'Percentage',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 75,
        isBold: false,
        isRightJustify: true,
        isPercent: true,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });
      this.columns.push({
        column: (foundDistrict.candidates.length + 4).toString(),
        label: 'Total',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 80,
        isBold: false,
        isRightJustify: true,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });

      const pollingPlaceTotals: number[] = [];
      for (let i = 0; i < foundDistrict.candidates.length + 4; i++) {
        pollingPlaceTotals.push(0);
      }
      foundDistrict.pollingPlaces.forEach(pollingPlace => {
        const tmpPivotTable: IShadowTableData[] = [];
        // Polling Place Name
        tmpPivotTable.push({
          isNumber: false,
          numberData: 0,
          stringData: pollingPlace.pollingPlaceName,
          isBold: false
        });

        // Candidate votes
        pollingPlace.pollingCandidates.forEach((pollingCandidate, index) => {
          tmpPivotTable.push({
            isNumber: true,
            numberData: pollingCandidate.formalVotes,
            stringData: '',
            isBold: false
          });
          pollingPlaceTotals[index] += pollingCandidate.formalVotes;
        });

        // Formal
        tmpPivotTable.push({
          isNumber: true,
          numberData: pollingPlace.totalFormalVotes,
          stringData: '',
          isBold: false
        });
        pollingPlaceTotals[foundDistrict.candidates.length] += pollingPlace.totalFormalVotes;

        // Informal
        tmpPivotTable.push({
          isNumber: true,
          numberData: pollingPlace.informalVotes,
          stringData: '',
          isBold: false
        });
        pollingPlaceTotals[foundDistrict.candidates.length + 1] += pollingPlace.informalVotes;

        // Percentage
        tmpPivotTable.push({
          isNumber: true,
          numberData: foundDistrict.districtTotalVotesCast > 0 ?
            ((pollingPlace.totalFormalVotes + pollingPlace.informalVotes) / foundDistrict.districtTotalVotesCast) * 100 : 0,
          stringData: '',
          isBold: false
        });
        pollingPlaceTotals[foundDistrict.candidates.length + 2] += foundDistrict.districtTotalVotesCast > 0 ?
          ((pollingPlace.totalFormalVotes + pollingPlace.informalVotes) / foundDistrict.districtTotalVotesCast) * 100 : 0,

        // Total
        tmpPivotTable.push({
          isNumber: true,
          numberData: pollingPlace.totalFormalVotes + pollingPlace.informalVotes,
          stringData: '',
          isBold: false
        });
        pollingPlaceTotals[foundDistrict.candidates.length + 3] += pollingPlace.totalFormalVotes;
        this.shadowTable.push(tmpPivotTable);
      });

      const tmpTotal: IShadowTableData[] = [];
      // Polling Place Total
      tmpTotal.push({
        isNumber: false,
        numberData: 0,
        stringData: 'Polling Place Totals',
        isBold: true
      });
      pollingPlaceTotals.forEach(total => {
        tmpTotal.push({
          isNumber: true,
          numberData: total,
          stringData: '',
          isBold: true
        });
      });
      this.shadowTable.push(tmpTotal);

      const tmpDeclTotal: IShadowTableData[] = [];
      // Polling Place Total
      tmpDeclTotal.push({
        isNumber: false,
        numberData: 0,
        stringData: 'Declaration Ballot Papers',
        isBold: true
      });
      foundDistrict.candidates.forEach((candidate, index) => {
        tmpDeclTotal.push({
          isNumber: true,
          numberData: candidate.declarationVoteCount,
          stringData: '',
          isBold: true
        });
        pollingPlaceTotals[index] += candidate.declarationVoteCount;
      });
      tmpDeclTotal.push({
        isNumber: true,
        numberData: foundDistrict.districtFormalDeclarationVotes,
        stringData: '',
        isBold: true
      });
      pollingPlaceTotals[foundDistrict.candidates.length] += foundDistrict.districtFormalDeclarationVotes;
      tmpDeclTotal.push({
        isNumber: true,
        numberData: foundDistrict.districtInformalDeclarationVotes,
        stringData: '',
        isBold: true
      });
      pollingPlaceTotals[foundDistrict.candidates.length + 1] += foundDistrict.districtInformalDeclarationVotes;
      tmpDeclTotal.push({
        isNumber: false,
        numberData: foundDistrict.districtTotalVotesCast > 0 ?
          ((foundDistrict.districtFormalDeclarationVotes + foundDistrict.districtInformalDeclarationVotes) /
          foundDistrict.districtTotalVotesCast) * 100 : 0,
        stringData: '',
        isBold: true
      });
      pollingPlaceTotals[foundDistrict.candidates.length + 2] += foundDistrict.districtTotalVotesCast > 0 ?
        ((foundDistrict.districtFormalDeclarationVotes + foundDistrict.districtInformalDeclarationVotes) /
        foundDistrict.districtTotalVotesCast) * 100 : 0;

      tmpDeclTotal.push({
        isNumber: true,
        numberData: foundDistrict.districtInformalDeclarationVotes + foundDistrict.districtFormalDeclarationVotes,
        stringData: '',
        isBold: true
      });
      pollingPlaceTotals[foundDistrict.candidates.length + 3] +=
        foundDistrict.districtInformalDeclarationVotes + foundDistrict.districtFormalDeclarationVotes;
      this.shadowTable.push(tmpDeclTotal);

      const tmpDistrictTotal: IShadowTableData[] = [];
      // Polling Place Total
      tmpDistrictTotal.push({
        isNumber: false,
        numberData: 0,
        stringData: 'District Total',
        isBold: true
      });
      pollingPlaceTotals.forEach(total => {
        tmpDistrictTotal.push({
          isNumber: true,
          numberData: total,
          stringData: '',
          isBold: true
        });
      });
      this.shadowTable.push(tmpDistrictTotal);
    }



    const output = [];
    for (let i = 0; i < this.shadowTable.length; i++) {
      const newObj: {[k: string]: any} = {};
      for (let j = 0; j < this.columns.length; j++) {
        const tmpName: string = this.columns[j].column;
        const x = tmpName;
        const tmp = this.shadowTable[i][j].isNumber ? +this.shadowTable[i][j].numberData : this.shadowTable[i][j].stringData;
        newObj[tmpName] = tmp;
      }
      output.push(newObj);
    }

    this.displayedColumns = this.columns.map(c => c.column);
    if (this.shadowTable?.length > 0) {
      this.dataSource = new MatTableDataSource(output);
    }
    this.cdr.detectChanges();
  }


  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

}
